import React, { useEffect, useRef } from "react";
import {
    Box,
    IconButton,
    Card,
    CardMedia,
    CardContent,
    Typography,
    CardActions,
    Button,
    useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Card as CardType } from "../utils/types";


interface CardCarouselProps {
    cards: CardType[];
    carouselRef: React.Ref<HTMLDivElement>;
    scrollCarousel: (dir: number) => void;
    onButtonClick: (buttonTitle: string, payload: string) => void;
    isLastCardMessage: boolean;
}




const CardCarousel: React.FC<CardCarouselProps> = ({ cards, carouselRef, scrollCarousel, onButtonClick, isLastCardMessage  }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    
    // Button color: #ea8b3e for light mode, keep CSS variable for dark mode
    const buttonBgColor = isDarkMode ? "var(--primary)" : "#ea8b3e";
    const buttonHoverColor = isDarkMode ? "var(--primary-dark)" : "#d17a35";
    
    // Internal ref for drag-to-scroll
    const internalCarouselRef = useRef<HTMLDivElement | null>(null);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    
    // Combine refs (external + internal)
    const setRefs = (element: HTMLDivElement | null) => {
        internalCarouselRef.current = element;
        if (typeof carouselRef === 'function') {
            carouselRef(element);
        } else if (carouselRef) {
            (carouselRef as React.MutableRefObject<HTMLDivElement | null>).current = element;
        }
    };
    
    // Drag-to-scroll functionality
    useEffect(() => {
        const carousel = internalCarouselRef.current;
        if (!carousel) return;
        
        const handleMouseDown = (e: MouseEvent) => {
            isDragging.current = true;
            startX.current = e.pageX - carousel.offsetLeft;
            scrollLeft.current = carousel.scrollLeft;
            carousel.style.cursor = 'grabbing';
            carousel.style.userSelect = 'none';
        };
        
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            e.preventDefault();
            const x = e.pageX - carousel.offsetLeft;
            const walk = (x - startX.current) * 2; // Scroll speed multiplier
            carousel.scrollLeft = scrollLeft.current - walk;
        };
        
        const handleMouseUp = () => {
            isDragging.current = false;
            carousel.style.cursor = 'grab';
            carousel.style.userSelect = 'auto';
        };
        
        const handleMouseLeave = () => {
            if (isDragging.current) {
                isDragging.current = false;
                carousel.style.cursor = 'grab';
                carousel.style.userSelect = 'auto';
            }
        };
        
        carousel.addEventListener('mousedown', handleMouseDown);
        carousel.addEventListener('mousemove', handleMouseMove);
        carousel.addEventListener('mouseup', handleMouseUp);
        carousel.addEventListener('mouseleave', handleMouseLeave);
        
        // Set initial cursor
        carousel.style.cursor = 'grab';
        
        return () => {
            carousel.removeEventListener('mousedown', handleMouseDown);
            carousel.removeEventListener('mousemove', handleMouseMove);
            carousel.removeEventListener('mouseup', handleMouseUp);
            carousel.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, []);
    
    return (
        <Box className="card-carousel-wrapper" display="flex" alignItems="center">
            {cards.length > 1 && (
                <IconButton onClick={() => scrollCarousel(-1)} size="small">
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
            )}
            <Box
                className={`card-carousel ${cards.length === 1 ? 'single-card' : ''}`}
                ref={setRefs}
                sx={{
                    display: "flex",
                    overflowX: "auto",
                    gap: 2,
                    pb: 1,
                    scrollSnapType: "x mandatory",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                    scrollBehavior: "smooth",
                    justifyContent: cards.length === 1 ? "center" : "flex-start",
                }}
            >
                {cards.map((c, idx) => (
                    <Card
                        key={idx}
                        className={cards.length === 1 ? 'single-card-item' : ''}
                        sx={{
                            flex: cards.length === 1 ? { xs: "0 0 280px", sm: "0 0 320px" } : "0 0 280px",
                            backgroundColor: "var(--white)",
                            boxShadow: cards.length === 1 
                                ? "0 1px 8px rgba(0, 0, 0, 0.15)" 
                                : "var(--card-shadow)",
                            borderRadius: cards.length === 1 ? "8px" : 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                            p: 0,
                            scrollSnapAlign: "center",
                            overflow: "hidden",
                        }}
                        elevation={0}
                    >
                        {c.image_url && (
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    paddingTop: "60%", // Aspect ratio più basso (5:3 circa)
                                    overflow: "hidden",
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    image={c.image_url}
                                    alt={c.title}
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        objectPosition: "center",
                                        display: "block",
                                    }}
                                />
                            </Box>
                        )}
                        <CardContent sx={{ p: 2, flexGrow: 1}}>
                            {c.title && (
                                <Typography
                                    variant="h6"
                                    component="div"
                                    sx={{
                                        fontWeight: 700,
                                        color: "var(--secondary)",
                                        fontSize: 17,
                                        marginTop: '0',
                                    }}
                                >
                                    {c.title}
                                </Typography>
                            )}
                            {/* La nuova logica inserisce un'interruzione di riga prima di ogni emoji. */}
                            {c.subtitle && (
                            <Box>
  {c.subtitle.split(/\n/g).map((line, lineIdx) => (
    <Typography
        key={lineIdx}
        variant="body2"
        sx={{
            color: "var(--text-medium)",
            fontSize: 13,
            fontWeight: 400,
            display: "block",
        }}
        >
        {line
            // 🔹 spezza sulle emoji
            .replace(
            /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g,
            `\n$&`
            )
            .split("\n")
            .map((part, partIdx) => {
            let processed = part;

            // 🔹 Se è la PRIMA RIGA → wrappa tutto in bold
            if (lineIdx === 0 && partIdx === 0 && processed.trim().length > 0) {
                processed = `|||${processed.trim()}|||`;
            }

            processed = processed.replace(
                /^(.*?)([\u0080-\uFFFF])/,
                (match, before, symbol) =>
                `|||${before.trim()}|||\n${symbol}`
            );

            // 🔹 Regola 1: bold dopo la data
            processed = processed.replace(
                /(\d{2}\/[A-Z]{3}\/\d{4}\s+\d{2}:\d{2})/g,
                (match) => `|||${match}|||`
            );

            // 🔹 Regola 2: bold tra ":" e "EUR"
            processed = processed.replace(/:\s*(.*?)EUR/g, (match, content) => {
                let value = content.trim();
                if (/^\d+\.\d{2}$/.test(value) && value.endsWith(".00")) {
                value = value.slice(0, -3);
                }
                return `: |||${value} EUR|||`;
            });

            // 🔹 Split e wrappa in <strong>
            const finalParts = processed.split("|||").map((p, i) =>
                i % 2 === 1 ? (
                <strong key={`b-${lineIdx}-${partIdx}-${i}`}>{p}</strong>
                ) : (
                p
                )
            );

            return (
                <React.Fragment key={partIdx}>
                {finalParts}
                <br />
                </React.Fragment>
            );
            })}
        </Typography>

  ))}
</Box>


                            )}
                        </CardContent>
                        {c.buttons && c.buttons.length > 0 && (
                            <CardActions
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                    gap: "4px",
                                    px: 2,
                                    pb: 2,
                                }}
                            >
                                {c.buttons.map((btn, btnIdx) => {
                                    if (btn.type === "web_url" && btn.url) {
                                        return (
                                            <Button
                                                key={btnIdx}
                                                href={btn.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                size="small"
                                                disabled={!isLastCardMessage}
                                                sx={{
                                                    backgroundColor: isLastCardMessage ? buttonBgColor : "var(--disabled-button-bg, #e0e0e0)",
                                                    color: isLastCardMessage ? "var(--white)" : "var(--disabled-button-color, #a0a0a0)",
                                                    fontWeight: 600,
                                                    borderRadius: "12px",
                                                    textTransform: "none",
                                                    minWidth: "100px",
                                                    "&:hover": {
                                                        backgroundColor: isLastCardMessage ? buttonHoverColor : "var(--disabled-button-bg, #e0e0e0)",
                                                        cursor: isLastCardMessage ? "pointer" : "not-allowed",
                                                    },
                                                }}
                                            >
                                                {btn.title}
                                            </Button>
                                        );
                                    } else if (btn.type === "postback" && btn.payload) {
                                        return (
                                            <Button
                                                key={btnIdx}
                                                onClick={() => onButtonClick(c.title!, btn.payload!)}
                                                size="small"
                                                disabled={!isLastCardMessage} 
                                                sx={{
                                                backgroundColor: isLastCardMessage ? buttonBgColor : "var(--disabled-button-bg, #e0e0e0)",
                                                    color: isLastCardMessage ? "var(--white)" : "var(--disabled-button-color, #a0a0a0)",
                                                    fontWeight: 600,
                                                    borderRadius: "12px",
                                                    textTransform: "none",
                                                    minWidth: "100px",
                                                    "&:hover": {
                                                        backgroundColor: isLastCardMessage ? buttonHoverColor : "var(--disabled-button-bg, #e0e0e0)",
                                                        cursor: isLastCardMessage ? "pointer" : "not-allowed",
                                                    },
                                                }}
                                            >
                                                {btn.title}
                                            </Button>
                                        );
                                    }
                                    return null;
                                })}
                            </CardActions>
                        )}
                    </Card>
                ))}
            </Box>
            {cards.length > 1 && (
                <IconButton onClick={() => scrollCarousel(1)} size="small">
                    <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
};

export default CardCarousel;
